import React, { useEffect, useState } from "react";
import { Loader2, Truck, ExternalLink, CircleDot, Check } from "lucide-react";
import { customerApi } from "@/lib/customerApi";

const dateLong = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleDateString("en-AU", { weekday: "long", day: "numeric", month: "long" });
};

const dateShort = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toLocaleString("en-AU", { day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
};

export const AccountDeliveries = () => {
  const [deliveries, setDeliveries] = useState(null);

  useEffect(() => {
    customerApi.get("/account/deliveries").then(({ data }) => setDeliveries(data)).catch(() => setDeliveries([]));
  }, []);

  if (deliveries === null) {
    return (
      <div className="mt-4 py-6 flex justify-center" data-testid="account-deliveries-loading">
        <Loader2 className="w-5 h-5 animate-spin text-ei-navy" />
      </div>
    );
  }

  if (!deliveries.length) {
    return (
      <p className="mt-4 text-sm text-ei-graphite/60" data-testid="account-deliveries-empty">
        No deliveries on the road yet. As soon as an order is booked with our carrier, its progress and tracking appear here.
      </p>
    );
  }

  return (
    <div className="mt-4 space-y-4" data-testid="account-deliveries">
      {deliveries.map((delivery) => {
        const expected = dateLong(delivery.eta_local);
        const status = (delivery.latest_status || "").toLowerCase();
        const delivered = status.includes("delivered") || status.includes("pod");
        const problem = ["exception", "failed", "damaged", "lost", "held", "return"].some((word) => status.includes(word));
        const pillClass = problem
          ? "text-amber-900 bg-amber-50 border-amber-200"
          : delivered
            ? "text-emerald-900 bg-emerald-50 border-emerald-200"
            : "text-ei-navy bg-ei-mist/40 border-ei-mist";
        return (
          <div key={delivery.consignment_number} className="border border-ei-mist rounded-md bg-white p-5" data-testid={`account-delivery-${delivery.consignment_number}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="font-semibold text-ei-navy flex items-center gap-2">
                  <Truck className="w-4 h-4 text-ei-golddark" />
                  <span data-testid={`delivery-reference-${delivery.consignment_number}`}>{delivery.reference || delivery.consignment_number}</span>
                </div>
                <p className="mt-1 text-xs text-ei-graphite/60">
                  {[delivery.carrier, delivery.service].filter(Boolean).join(" · ")}
                  {delivery.delivery_suburb && <> · to {delivery.delivery_suburb} {delivery.delivery_postcode}</>}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <span className={`inline-flex items-center gap-1.5 text-xs font-semibold border px-3 py-1 rounded-full ${pillClass}`} data-testid={`delivery-status-${delivery.consignment_number}`}>
                  <CircleDot className="w-3.5 h-3.5" /> {delivery.latest_status}
                </span>
                {delivery.tracking_url && (
                  <a href={delivery.tracking_url} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-bold bg-ei-navy text-ei-white px-3 py-2 rounded-sm hover:bg-ei-navy/90 transition-colors min-h-[40px]"
                    data-testid={`delivery-track-${delivery.consignment_number}`}>
                    <ExternalLink className="w-3.5 h-3.5" /> Track
                  </a>
                )}
              </div>
            </div>

            {expected && !delivered && (
              <p className="mt-3 text-sm text-ei-navy" data-testid={`delivery-eta-${delivery.consignment_number}`}>
                Expected <strong>{expected}</strong>, kerbside during business hours.
              </p>
            )}
            {delivered && (
              <p className="mt-3 text-sm font-semibold text-emerald-800" data-testid={`delivery-eta-${delivery.consignment_number}`}>
                Delivered.
              </p>
            )}

            {delivery.statuses?.length > 0 && (
              <ol className="mt-4 border-t border-ei-mist pt-4 space-y-2" data-testid={`delivery-timeline-${delivery.consignment_number}`}>
                {delivery.statuses.slice(0, 6).map((status, index) => (
                  <li key={`${status.name}-${status.at}-${index}`} className="flex items-start gap-2.5 text-xs">
                    <Check className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${index === 0 ? "text-emerald-700" : "text-ei-graphite/40"}`} />
                    <span className={index === 0 ? "text-ei-navy font-semibold" : "text-ei-graphite/70"}>
                      {status.name}
                      {status.at && <span className="ml-2 font-normal text-ei-graphite/50">{dateShort(status.at)}</span>}
                      {status.detail && <span className="block font-normal text-ei-graphite/60">{status.detail}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            )}

            <p className="mt-3 text-xs text-ei-graphite/50">Consignment {delivery.consignment_number} · dispatched from {delivery.dispatch_warehouse}</p>
          </div>
        );
      })}
    </div>
  );
};
